/**
 * CategorySection — collapsible accordion for a row category.
 *
 * Renders as a bordered card with a header showing the category icon, title,
 * optional conflict badge, and row count. Expanding reveals the category's rows
 * as a table.
 */

import { AlertTriangle, Stars01 } from '@untitledui/icons';
import { memo, useCallback } from 'react';

import {
  AccordionChevron,
  AccordionItem,
  AccordionPanel,
  AccordionTrigger,
} from '@/lib/base/accordion';
import { Badge } from '@/lib/base/badges/badges';
import { Button } from '@/lib/base/buttons/button';
import type { PropertySchemaMap } from '@/lib/crm/hooks/usePropertySchema';
import type { CsvImport } from '@/lib/csv-import/api';
import type { CrmObject } from '@/types/crm';
import { cx } from '@/utils/cx';

import { LazyPanelContent } from '../LazyPanelContent';
import { InvalidRowsAiPrompt } from './InvalidRowsAiPrompt';
import type { ConflictsByRow } from './RowTable';
import { RowTableGroup } from './RowTable';
import type { RowCategory, RowData } from './types';
import { CATEGORY_CONFIGS, COLOR_CLASSES } from './types';

// ============================================================================
// Types
// ============================================================================

interface CategorySectionProps {
  category: RowCategory;
  rows: RowData[];
  importData: CsvImport;
  schemaMap: PropertySchemaMap;
  /** Per-entity-type schema maps — see RowTableGroup's prop of the same name. */
  schemaMapByEntityType?: Record<string, PropertySchemaMap>;
  newPropertyLabels: Record<string, string>;
  existingObjectsMap: Record<string, CrmObject> | undefined;
  /** Org id, for the update table's record chip (opens the slideout). */
  orgId: number;
  /** Conflicting property names per row id — see ConflictsByRow. */
  conflictsByRow?: ConflictsByRow;
  conflictCount?: number;
  getEntityTypeLabel: (name: string) => string;
  /** Controlled expansion. When omitted, the accordion manages its own state. */
  isExpanded?: boolean;
  /** Called when the user toggles the accordion. Required if isExpanded is set. */
  onExpandedChange?: (category: RowCategory, isExpanded: boolean) => void;
  /** Row id (`${fileId}-${row_index}`) to highlight + scroll into view inside the
   *  table. Used when an external trigger (e.g. clicking a pending-association
   *  cell) navigates to a specific row. */
  focusedRowId?: string | null;
  /** Invalid-category only: kick off / resume the agent-coercion review. */
  onReviewCoercions?: () => void;
  /** Label for the coercion CTA (e.g. "Let our agent fix these" vs "Review suggested fixes"). */
  coercionCtaLabel?: string;
  /** Whether the coercion suggestion request is in flight (disables + spins the CTA). */
  isReviewingCoercions?: boolean;
}

// ============================================================================
// Component
// ============================================================================

export const CategorySection = memo(function CategorySection({
  category,
  rows,
  importData,
  schemaMap,
  schemaMapByEntityType,
  newPropertyLabels,
  existingObjectsMap,
  orgId,
  conflictsByRow,
  conflictCount,
  getEntityTypeLabel,
  isExpanded,
  onExpandedChange,
  focusedRowId,
  onReviewCoercions,
  coercionCtaLabel,
  isReviewingCoercions,
}: CategorySectionProps) {
  // Forward the category id when the accordion toggles so the parent can
  // track expanded-category state.
  const handleExpandedChange = useCallback(
    (next: boolean) => onExpandedChange?.(category, next),
    [category, onExpandedChange]
  );

  const config = CATEGORY_CONFIGS[category];
  const colors = COLOR_CLASSES[config.color];
  const Icon = config.icon;

  if (rows.length === 0) {
    return null;
  }

  // NOTE: every category renders as a table.
  //
  // `update` used to be the exception, on the grounds that its rows reference an
  // existing CRM record worth surfacing inline. But that reference is a badge in
  // the collapsed header — which a table shows as a column — and the expanded
  // panel turned out to be just the incoming CSV values in a vertical list, not
  // a diff against the existing record. So the accordion hid every value behind
  // a click and showed nothing a table row doesn't, while the neighbouring
  // "New records" section displayed everything at a glance.

  return (
    <AccordionItem
      aria-label={`${config.title}, ${rows.length} rows`}
      className='border border-[var(--color-border-secondary)] rounded-lg overflow-hidden'
      isExpanded={isExpanded}
      onExpandedChange={onExpandedChange ? handleExpandedChange : undefined}
    >
      <AccordionTrigger className='w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-[var(--color-bg-secondary)]'>
        <AccordionChevron
          variant='leading'
          className='w-4 h-4 text-[var(--color-text-tertiary)] mt-0.5'
        />
        <Icon
          aria-hidden={true}
          className={cx('w-4 h-4 flex-shrink-0 mt-0.5', colors.icon)}
        />
        <div className='flex-1 min-w-0'>
          <span className='text-sm font-medium text-[var(--color-text-primary)]'>
            {config.title}
          </span>
          {config.description && (
            <p className='text-xs text-[var(--color-text-tertiary)] mt-0.5'>
              {config.description}
            </p>
          )}
        </div>
        {/* Conflict badge */}
        {conflictCount !== undefined && conflictCount > 0 && (
          <Badge color='warning' size='sm' className='gap-1 mt-0.5'>
            <AlertTriangle aria-hidden={true} className='w-3 h-3' />
            {conflictCount} {conflictCount === 1 ? 'conflict' : 'conflicts'}
          </Badge>
        )}
        <span
          className={cx(
            'text-sm font-semibold tabular-nums flex-shrink-0 mt-0.5',
            colors.count
          )}
        >
          {rows.length}
        </span>
      </AccordionTrigger>

      <AccordionPanel className='border-t border-[var(--color-border-secondary)]'>
        <LazyPanelContent>
          <RowTableGroup
            rows={rows}
            importData={importData}
            schemaMap={schemaMap}
            schemaMapByEntityType={schemaMapByEntityType}
            newPropertyLabels={newPropertyLabels}
            category={category}
            getEntityTypeLabel={getEntityTypeLabel}
            focusedRowId={focusedRowId}
            existingObjectsMap={existingObjectsMap}
            orgId={orgId}
            conflictsByRow={conflictsByRow}
          />
          {category === 'invalid' && onReviewCoercions && (
            <div className='mb-3'>
              <Button
                color='primary'
                size='sm'
                iconLeading={Stars01}
                onClick={onReviewCoercions}
                isLoading={isReviewingCoercions}
                isDisabled={isReviewingCoercions}
              >
                {coercionCtaLabel ?? 'Let our agent fix these'}
              </Button>
            </div>
          )}
          {category === 'invalid' && (
            <InvalidRowsAiPrompt
              rows={rows}
              importData={importData}
              schemaMap={schemaMap}
              newPropertyLabels={newPropertyLabels}
            />
          )}
        </LazyPanelContent>
      </AccordionPanel>
    </AccordionItem>
  );
});
